/**
 * Timezone Utilities
 * Auto-detect and manage user timezone without user interference
 */

/**
 * Get the user's IANA timezone (e.g., "America/New_York", "Asia/Dubai")
 * This works in all modern browsers without any user interaction
 */
export const getUserTimezone = (): string => {
  try {
    // Modern browsers support Intl.DateTimeFormat
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return timezone;
  } catch (error) {
    console.warn('Unable to detect timezone, falling back to UTC', error);
    return 'UTC';
  }
};

/**
 * Get the user's timezone offset in minutes from UTC
 * Positive values are west of UTC, negative values are east
 * Example: -300 for EST (UTC-5)
 */
export const getTimezoneOffset = (): number => {
  const offset = new Date().getTimezoneOffset();
  return offset;
};

/**
 * Get human-readable timezone offset string
 * Example: "UTC-5", "UTC+5:30"
 */
export const getTimezoneOffsetString = (): string => {
  const offset = getTimezoneOffset();
  const hours = Math.floor(Math.abs(offset) / 60);
  const minutes = Math.abs(offset) % 60;
  const sign = offset <= 0 ? '+' : '-';

  if (minutes === 0) {
    return `UTC${sign}${hours}`;
  }
  return `UTC${sign}${hours}:${minutes.toString().padStart(2, '0')}`;
};

/**
 * Get complete timezone information
 */
export const getTimezoneInfo = () => {
  return {
    timezone: getUserTimezone(),
    offset: getTimezoneOffset(),
    offsetString: getTimezoneOffsetString(),
    timestamp: new Date().toISOString(),
  };
};

/**
 * Convert UTC datetime to user's local timezone
 */
export const convertUTCToLocal = (utcDateString: string): Date => {
  return new Date(utcDateString);
};

/**
 * Convert local datetime to UTC
 */
export const convertLocalToUTC = (localDate: Date): string => {
  return localDate.toISOString();
};

/**
 * Format datetime in user's timezone
 */
export const formatInUserTimezone = (
  date: Date | string,
  options?: Intl.DateTimeFormatOptions
): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const timezone = getUserTimezone();

  const defaultOptions: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  };

  return dateObj.toLocaleString('en-US', { ...defaultOptions, ...options });
};

/**
 * Save timezone to localStorage for persistence
 */
export const saveUserTimezone = () => {
  const timezoneInfo = getTimezoneInfo();
  localStorage.setItem('userTimezone', JSON.stringify(timezoneInfo));
  return timezoneInfo;
};

/**
 * Get saved timezone from localStorage
 */
export const getSavedTimezone = () => {
  try {
    const saved = localStorage.getItem('userTimezone');
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (error) {
    console.warn('Error reading saved timezone', error);
  }
  return null;
};
